import React, { useCallback, useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input } from 'reactstrap';
import config from '../../config';
import s from './NewCustomerModal.module.scss';

const LANG_OPTIONS = ['english', 'kannada', 'roman_kannada'];

const DOMAIN_OPTIONS = [
  'AGRICULTURE',
  'FISHERIES & COASTAL',
  'FOOD & BEVERAGE',
  'CONSTRUCTION & WOODWORKS',
  'INDUSTRIAL & ENGINEERING',
  'ELECTRONICS',
  'AUTOMOTIVE',
  'APPAREL & GENERAL RETAIL',
  'HEALTHCARE & MEDICAL',
  'EVENTS & HOSPITALITY',
  'EDUCATION & INSTITUTIONS',
  'SERVICES, ADVERTISING',
  'OTHERS',
];

function normalizeCustomerTaxonomyResponse(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  if (data.taxonomy && typeof data.taxonomy === 'object' && !Array.isArray(data.taxonomy)) {
    return data.taxonomy;
  }
  const keys = Object.keys(data);
  if (keys.length === 1 && keys[0] === 'detail') return {};
  return data;
}

const NewCustomerModal = ({ isOpen, toggle, onSuccess }) => {
  const [customerType, setCustomerType] = useState('normal'); // 'normal' | 'b2b'
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('kannada');
  const [useCustomLanguageInput, setUseCustomLanguageInput] = useState(false);
  const [businessCategory, setBusinessCategory] = useState('');
  const [domain, setDomain] = useState('');
  const [remarks, setRemarks] = useState('');
  const [matchedCategory, setMatchedCategory] = useState('');
  const [taxonomyData, setTaxonomyData] = useState({});
  const [rating, setRating] = useState(5);
  const [starred, setStarred] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginPasswordConfirm, setLoginPasswordConfirm] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetLink, setResetLink] = useState(null);

  const [addressInput, setAddressInput] = useState('');
  const [addressSelection, setAddressSelection] = useState(null); // { id, text, lat, lng }
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [phoneWarning, setPhoneWarning] = useState(null);
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);

  const isB2b = customerType === 'b2b';

  const resetForm = () => {
    setCustomerType('normal');
    setName('');
    setBusinessName('');
    setPhone('');
    setPreferredLanguage('kannada');
    setUseCustomLanguageInput(false);
    setBusinessCategory('');
    setDomain('');
    setRemarks('');
    setMatchedCategory('');
    setRating(5);
    setStarred(false);
    setLoginEmail('');
    setLoginPassword('');
    setLoginPasswordConfirm('');
    setResetBusy(false);
    setResetLink(null);
    setAddressInput('');
    setAddressSelection(null);
    setAddressSuggestions([]);
    setError(null);
    setSuccess(null);
    setPhoneWarning(null);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Check if phone number already exists as customer
  useEffect(() => {
    const checkPhone = async () => {
      if (!phone || phone.length < 10) {
        setPhoneWarning(null);
        return;
      }

      setPhoneCheckLoading(true);
      try {
        // Normalize phone for checking (add 91 if 10 digits)
        const digits = phone.replace(/\D/g, '');
        const normalizedPhone = digits.length === 10 ? `91${digits}` : digits;

        const response = await fetch(`${config.customerApiBase}/customers`);
        if (response.ok) {
          const data = await response.json();
          const existing = data.customers?.find(c => c.phone === normalizedPhone);

          if (existing) {
            setPhoneWarning(`⚠️ Phone number already exists for customer: ${existing.name}`);
          } else {
            setPhoneWarning(null);
          }
        }
      } catch (err) {
        console.error('Error checking phone:', err);
      } finally {
        setPhoneCheckLoading(false);
      }
    };

    const timer = setTimeout(checkPhone, 500); // Debounce
    return () => clearTimeout(timer);
  }, [phone]);

  // Fetch taxonomy once on mount
  useEffect(() => {
    if (isOpen) {
      fetch(`${config.customerApiBase}/taxonomy`)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`taxonomy ${res.status}`))))
        .then((data) => setTaxonomyData(normalizeCustomerTaxonomyResponse(data)))
        .catch((err) => {
          console.error('Failed to fetch taxonomy:', err);
          setTaxonomyData({});
        });
    }
  }, [isOpen]);

  const handleResolveAddress = useCallback(async () => {
    if (!addressInput.trim()) {
      setError('Please enter an address to search.');
      return;
    }

    resetMessages();
    setAddressLoading(true);
    setAddressSuggestions([]);

    try {
      const response = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address_text: addressInput.trim() }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();

      if (data.type === 'similar' && Array.isArray(data.matches)) {
        setAddressSelection(null);
        setAddressSuggestions(data.matches);
        return;
      }

      setAddressSuggestions([]);
      setAddressSelection({
        id: data.address_id,
        text: data.resolved_address,
        lat: data.latitude,
        lng: data.longitude,
      });
      setSuccess('Address resolved successfully.');
    } catch (err) {
      setError(`Failed to resolve address: ${err.message}`);
      setAddressSelection(null);
    } finally {
      setAddressLoading(false);
    }
  }, [addressInput]);

  const handleSuggestionSelect = async (suggestion) => {
    setAddressLoading(true);
    let resolvedId = suggestion?.id ?? suggestion?.address_id ?? null;
    let resolvedText = suggestion?.locality || suggestion?.resolved_address || '';
    let resolvedLat = suggestion?.latitude;
    let resolvedLng = suggestion?.longitude;

    // Google-sourced suggestions may not have an address_id yet.
    // Resolve by coordinates to get a canonical address_id from the backend.
    if (!resolvedId && resolvedLat != null && resolvedLng != null) {
      try {
        const response = await fetch(`${config.customerApiBase}/resolve-location`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lat: Number(resolvedLat),
            lng: Number(resolvedLng),
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to resolve selected suggestion');
        }

        const data = await response.json();
        if (!data?.address_id) {
          throw new Error('Selected suggestion did not return a valid address ID');
        }

        resolvedId = data.address_id;
        resolvedText = data.resolved_address || resolvedText;
        resolvedLat = data.latitude ?? resolvedLat;
        resolvedLng = data.longitude ?? resolvedLng;
      } catch (err) {
        setError(`Failed to select address: ${err.message}`);
        setAddressSelection(null);
        setAddressLoading(false);
        return;
      }
    }

    setAddressSelection({
      id: resolvedId,
      text: resolvedText,
      lat: resolvedLat,
      lng: resolvedLng,
    });
    setAddressSuggestions([]);
    setSuccess('Address selected.');
    setError(null);
    setAddressLoading(false);
  };

  const handleForgotPassword = useCallback(async () => {
    const email = loginEmail.trim().toLowerCase();
    setError(null);
    setResetLink(null);
    if (!email || !email.includes('@')) {
      setError('Enter a login email first, then click Forgot password.');
      return;
    }
    if (!config.opsOnboardKey) {
      setError('REACT_APP_OPS_ONBOARD_KEY is not configured in the ops frontend.');
      return;
    }
    setResetBusy(true);
    try {
      const resp = await fetch(`${config.b2bApiBase}/ops/send-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Ops-Onboard-Key': config.opsOnboardKey,
        },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Could not generate password reset link');
      }
      setResetLink(data.reset_link || null);
      setSuccess(`Password reset link ready for ${email}. Copy and share with the customer.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setResetBusy(false);
    }
  }, [loginEmail]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    resetMessages();
    setResetLink(null);

    if (!phone.trim()) {
      setError('Phone number is required.');
      return;
    }

    if (isB2b) {
      if (!businessName.trim()) {
        setError('Business name is required for B2B customers.');
        return;
      }
      const email = loginEmail.trim().toLowerCase();
      if (!email || !email.includes('@')) {
        setError('Login email is required for B2B customers.');
        return;
      }
      if (!loginPassword) {
        setError('Login password is required for B2B customers.');
        return;
      }
      if (loginPassword !== loginPasswordConfirm) {
        setError('Password and confirm password do not match.');
        return;
      }
      if (!config.opsOnboardKey) {
        setError('REACT_APP_OPS_ONBOARD_KEY is not configured in the ops frontend.');
        return;
      }

      const payload = {
        name: name.trim() || null,
        phone: phone.trim(),
        preferred_language: preferredLanguage || 'english',
        primary_address_id: addressSelection?.id || null,
        business_name: businessName.trim(),
        business_category: businessCategory.trim() || null,
        domain: domain || null,
        remarks: remarks.trim() || null,
        matched_category: matchedCategory || null,
        rating: Number(rating) || 5,
        star: starred,
        email,
        password: loginPassword,
      };

      try {
        setSubmitting(true);
        const resp = await fetch(`${config.b2bApiBase}/ops/onboard`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Ops-Onboard-Key': config.opsOnboardKey,
          },
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(
            typeof data.detail === 'string' ? data.detail : data.detail?.[0]?.msg || 'Failed to create B2B customer'
          );
        }
        setSuccess(
          `B2B customer created. Business #${data.business_id}, customer #${data.customer_id}. Login: ${data.email}`
        );
        setTimeout(() => {
          handleClose();
          if (onSuccess) onSuccess();
        }, 1400);
      } catch (err) {
        setError(err.message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const payload = {
      name: name.trim() || null,
      phone: phone.trim(),
      preferred_language: preferredLanguage,
      primary_address_id: addressSelection?.id || null,
      business_name: businessName.trim() || null,
      business_category: businessCategory.trim() || null,
      domain: domain || null,
      remarks: remarks.trim() || null,
      matched_category: matchedCategory || null,
      rating: Number(rating) || 5,
      star: starred,
    };

    try {
      setSubmitting(true);
      const resp = await fetch(`${config.customerApiBase}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to create customer');
      }

      const data = await resp.json();

      // Show warning if present in response
      if (data.warning) {
        setSuccess(`${data.warning} Customer created successfully.`);
      } else {
        setSuccess('Customer created successfully.');
      }

      // Close modal and refresh list after a brief delay
      setTimeout(() => {
        handleClose();
        if (onSuccess) onSuccess();
      }, 1200);

    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [
    addressSelection,
    businessCategory,
    businessName,
    domain,
    isB2b,
    loginEmail,
    loginPassword,
    loginPasswordConfirm,
    matchedCategory,
    name,
    phone,
    preferredLanguage,
    rating,
    remarks,
    starred,
    onSuccess,
  ]);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" className={s.modal}>
      <ModalHeader toggle={handleClose}>
        <div className={s.headerContent}>
          <div className={s.title}>New Customer</div>
          <div className={s.subtitle}>
            {isB2b
              ? 'B2B portal account — business profile + login email/password (Firebase).'
              : 'Only phone number is required. All other fields are optional.'}
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit} id="newCustomerForm">
          <div className={s.typeToggle} role="group" aria-label="Customer type">
            <button
              type="button"
              className={`${s.typeBtn} ${!isB2b ? s.typeBtnActive : ''}`}
              onClick={() => setCustomerType('normal')}
            >
              Normal
            </button>
            <button
              type="button"
              className={`${s.typeBtn} ${isB2b ? s.typeBtnActive : ''}`}
              onClick={() => {
                setCustomerType('b2b');
                if (preferredLanguage === 'kannada') setPreferredLanguage('english');
              }}
            >
              B2B customer
            </button>
          </div>

          <div className={s.grid}>
            <div className={s.formGroup}>
              <label>Name {isB2b ? '' : ''}</label>
              <Input
                type="text"
                placeholder="Contact person name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <div className={s.hint}>If contact name is not available, fill business name.</div>
            </div>

            <div className={s.formGroup}>
              <label>
                Business Name {isB2b && <span className={s.required}>*</span>}
              </label>
              <Input
                type="text"
                placeholder="e.g., Sri Lakshmi Traders"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
              {isB2b && (
                <div className={s.hint}>Used as B2B organization name on the business portal.</div>
              )}
            </div>

            <div className={s.formGroup}>
              <label>Phone Number <span className={s.required}>*</span></label>
              <Input
                type="text"
                placeholder="10 or 12 digit phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <div className={s.hint}>10-digit numbers will be prefixed with 91 automatically.</div>
              {phoneCheckLoading && <div className={s.phoneCheck}>Checking...</div>}
              {phoneWarning && <div className={s.phoneWarning}>{phoneWarning}</div>}
            </div>

            <div className={s.formGroup}>
              <label>Preferred Language</label>
              <select
                value={LANG_OPTIONS.includes(preferredLanguage) ? preferredLanguage : '__custom__'}
                onChange={(e) => {
                  const selected = e.target.value;
                  if (selected === '__custom__') {
                    setUseCustomLanguageInput(true);
                    if (LANG_OPTIONS.includes(preferredLanguage)) {
                      setPreferredLanguage('');
                    }
                  } else {
                    setUseCustomLanguageInput(false);
                    setPreferredLanguage(selected);
                  }
                }}
              >
                <option value="">Select language</option>
                {LANG_OPTIONS.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
                <option value="__custom__">Custom (type below)</option>
              </select>
              {(useCustomLanguageInput || !LANG_OPTIONS.includes(preferredLanguage)) && (
                <Input
                  type="text"
                  placeholder="Type custom language"
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                  style={{ marginTop: '6px' }}
                />
              )}
              <div className={s.hint}>Choose a language, or select "Custom" and type directly.</div>
            </div>

            <div className={s.formGroup}>
              <label>Business Category</label>
              <Input
                type="text"
                placeholder="ex tiles showroom,furniture shop.."
                value={businessCategory}
                onChange={(e) => setBusinessCategory(e.target.value)}
              />
              <div className={s.hint}>Enter the customer's business type. Domain and sub-category will be auto-resolved by AI.</div>
            </div>

            <div className={s.formGroup}>
              <label>Domain</label>
              <Input
                type="select"
                value={domain}
                onChange={(e) => {
                  setDomain(e.target.value);
                  setMatchedCategory(''); // Reset sub-category when domain changes
                }}
              >
                <option value="">Auto sync</option>
                {(Object.keys(taxonomyData).length > 0 ? Object.keys(taxonomyData).sort() : DOMAIN_OPTIONS).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Input>
              <div className={s.hint}>Override only if you know the exact domain. Otherwise AI assigns from business category.</div>
            </div>

            <div className={s.formGroup}>
              <label>Matched Category (Sub-category)</label>
              <Input
                type="select"
                value={matchedCategory}
                onChange={(e) => setMatchedCategory(e.target.value)}
              >
                <option value="">{domain ? 'Auto sync' : 'Pick a domain first, or auto sync both'}</option>
                {domain && taxonomyData[domain] && [...taxonomyData[domain]].sort().map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </Input>
              <div className={s.hint}>Manual picks here will be remembered by the AI for future customers with similar categories.</div>
            </div>

            <div className={s.formGroup}>
              <label>Remarks</label>
              <Input
                type="textarea"
                rows="2"
                placeholder="Optional notes"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>

            <div className={s.formGroup}>
              <label>Rating</label>
              <Input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
              />
            </div>

            <div className={s.formGroup}>
              <label>Important Customer</label>
              <div>
                <input
                  id="new-customer-star"
                  type="checkbox"
                  checked={starred}
                  onChange={(e) => setStarred(e.target.checked)}
                />
                <label htmlFor="new-customer-star" style={{ marginLeft: '8px', marginBottom: 0 }}>
                  Mark as important customer
                </label>
              </div>
            </div>
          </div>

          <div className={s.addressCard}>
            <div className={s.addressHeader}>
              <h3>Primary Address</h3>
              {addressSelection && (
                <span className={s.addressChip}>ID: {addressSelection.id}</span>
              )}
            </div>

            <div className={s.addressRow}>
              <Input
                type="text"
                placeholder="Search locality, landmark, or city..."
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
              />
              <button
                type="button"
                className={s.searchBtn}
                onClick={handleResolveAddress}
                disabled={addressLoading || !addressInput.trim()}
              >
                {addressLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {addressLoading && !addressSuggestions.length && (
              <div className={s.searchingHint}>
                <i className="fa fa-spinner fa-spin" /> Searching...
              </div>
            )}

            {addressSuggestions.length > 0 && (
              <div className={s.suggestions}>
                <div className={s.suggestionsHeader}>
                  <i className="fa fa-map-marker" />
                  <span>We found {addressSuggestions.length} similar addresses — click one to select</span>
                </div>
                {addressSuggestions.map((suggestion, idx) => (
                  <div
                    key={suggestion.id || `sug-${idx}`}
                    className={`${s.suggestionItem} ${addressLoading ? s.suggestionItemDisabled : ''}`}
                    onClick={() => !addressLoading && handleSuggestionSelect(suggestion)}
                  >
                    <div className={s.suggestionTitle}>
                      {suggestion.locality}
                      {suggestion.source === 'google_suggest' && (
                        <span className={s.googleBadge} title="From Google Maps — not yet in our database">
                          {' '}🌐 Suggested
                        </span>
                      )}
                    </div>
                    <div className={s.suggestionMeta}>
                      {[suggestion.city, suggestion.state, suggestion.pin_code].filter(Boolean).join(', ')}
                      <span className={s.matchScore}>{Math.round(suggestion.similarity_score * 100)}% match</span>
                    </div>
                  </div>
                ))}
                <button type="button" className={s.closeSuggestions} onClick={() => setAddressSuggestions([])}>
                  Dismiss
                </button>
              </div>
            )}

            {addressSelection && (
              <div className={s.selectionBanner}>
                <i className="fa fa-check-circle" />
                Selected: {addressSelection.text}
              </div>
            )}
          </div>

          {isB2b && (
            <div className={s.credentialsCard}>
              <div className={s.credentialsTitle}>Portal login credentials</div>
              <div className={s.credentialsHint}>
                Email + password for https://devbusiness.unloadin.com (no separate username).
              </div>
              <div className={s.grid}>
                <div className={s.formGroup}>
                  <label>
                    Login email <span className={s.required}>*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder="ops@company.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className={s.formGroup}>
                  <label>
                    Password <span className={s.required}>*</span>
                  </label>
                  <Input
                    type="password"
                    placeholder="8+ chars, upper, number, special"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className={s.formGroup}>
                  <label>
                    Confirm password <span className={s.required}>*</span>
                  </label>
                  <Input
                    type="password"
                    placeholder="Re-enter password"
                    value={loginPasswordConfirm}
                    onChange={(e) => setLoginPasswordConfirm(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className={s.credentialsActions}>
                <button
                  type="button"
                  className={s.forgotBtn}
                  onClick={handleForgotPassword}
                  disabled={resetBusy}
                >
                  {resetBusy ? 'Generating…' : 'Forgot password (reset link)'}
                </button>
              </div>
              {resetLink && (
                <div className={s.resetLinkBox}>
                  <div className={s.hint}>Share this reset link with the customer:</div>
                  <a href={resetLink} target="_blank" rel="noreferrer" className={s.resetLink}>
                    {resetLink}
                  </a>
                </div>
              )}
            </div>
          )}

          {error && <div className={s.error}>{error}</div>}
          {success && <div className={s.success}>{success}</div>}
        </form>
      </ModalBody>
      <ModalFooter>
        <button type="button" className={s.secondaryBtn} onClick={handleClose}>
          Cancel
        </button>
        <button type="submit" form="newCustomerForm" className={s.primaryBtn} disabled={submitting}>
          {submitting ? 'Creating…' : isB2b ? 'Create B2B Customer' : 'Create Customer'}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default NewCustomerModal;
